/**
 * The HTML template API (`/api/html_templates`), and the two media-library
 * calls the editor needs. Same `request` shape as `design/designApi.js`: a
 * 422 becomes a `TemplateValidationError` carrying EVERY message, because the
 * server reports every problem and a person fixing a template wants the list.
 *
 * Generation, edits and renders are JOBS: they run in the worker (Chromium,
 * the model) and the browser polls. `pollJob` is the one loop for all three.
 */
const API_BASE_URL = import.meta.env?.VITE_API_BASE_URL || '/api';

/** A run somebody stopped. Not a failure — nothing to explain or retry. */
export class JobCancelled extends Error {
  constructor() {
    super('stopped');
    this.name = 'JobCancelled';
    this.cancelled = true;
  }
}

export class TemplateValidationError extends Error {
  constructor(errors) {
    super(errors.join('; '));
    this.name = 'TemplateValidationError';
    this.errors = errors;
  }
}

/**
 * A failure that says nothing about the request — the gateway, the network,
 * or a tab the browser suspended mid-fetch.
 *
 * It has to be distinguishable from a refusal, because the two deserve
 * opposite handling: a 422 or a 404 will never succeed however many times it
 * is sent, and a 502 usually succeeds on the next attempt. Conflating them is
 * how one bad second abandoned a three-minute generation.
 */
const transient = (err) => !!err?.transient;

const request = async (path, options = {}, base = '/html_templates') => {
  let res;
  try {
    res = await fetch(`${API_BASE_URL}${base}${path}`, {
      credentials: 'include',
      headers: options.body && !(options.body instanceof FormData) ? { 'Content-Type': 'application/json' } : undefined,
      ...options,
    });
  } catch (err) {
    // fetch rejects for DNS, CORS, offline, and for ERR_NETWORK_IO_SUSPENDED
    // — which is what a tab frozen by the browser does to a request already
    // in flight. None of them are the request's fault.
    const wrapped = new Error('The connection dropped.');
    wrapped.transient = true;
    wrapped.cause = err;
    throw wrapped;
  }
  const body = await res.json().catch(() => ({}));
  if (res.status === 422) {
    const byField = body?.messages?.json || {};
    const errors = Object.values(byField).flat().filter(Boolean);
    throw new TemplateValidationError(errors.length ? errors : [body.error || 'That template was rejected.']);
  }
  if (!res.ok) {
    const err = new Error(body.error || `Request failed (${res.status})`);
    err.status = res.status;
    // 5xx is the gateway or a worker that went; 429 is "later". Everything
    // else is an answer, even an unwelcome one.
    err.transient = res.status >= 500 || res.status === 429;
    throw err;
  }
  return body;
};

const json = (body) => JSON.stringify(body);

export const config = () => request('/config');
export const listTemplates = () => request('/templates').then((b) => b.templates || []);
/** `generated` says which of the template's pictures were GENERATED, and from what. */
export const readTemplate = (id) => request(`/templates/${id}`)
  .then((b) => ({ row: b.template, media: b.media || {}, generated: b.generated || {} }));
/** What the template was made from: its brief's and its chats' images and clips. */
export const templateSources = (id) => request(`/templates/${id}/sources`).then((b) => b.sources || []);
/**
 * `jobId` links the run this template came out of, which is the only moment
 * either side can know it: the job cannot set it while it runs, because the
 * template does not exist until this call. Without it, a finished chat on the
 * landing page has nothing to open.
 */
export const createTemplate = ({ name, template, conversation, jobId = null }) =>
  request('/templates', { method: 'POST', body: json({ name, template, conversation, job_id: jobId || undefined }) }).then((b) => b.template);
export const updateTemplate = (id, patch) =>
  request(`/templates/${id}`, { method: 'PUT', body: json(patch) }).then((b) => b.template);
export const deleteTemplate = (id) => request(`/templates/${id}`, { method: 'DELETE' });
export const renameTemplate = (id, name) => updateTemplate(id, { name });
/**
 * A copy of a saved template, with its conversation, under a new name. Read
 * then create, so the copy is of what is STORED — from a list there is no
 * other copy to take.
 */
export const duplicateTemplate = async (id, name = null) => {
  const { row } = await readTemplate(id);
  return createTemplate({
    name: name || `${row.name} copy`,
    template: { ...row.template, name: name || `${row.name} copy` },
    conversation: row.conversation || [],
  });
};

/**
 * Put a template back to how it was before one of its turns.
 *
 * Returns the whole row — template, conversation and resolved media — because
 * a restore replaces all three: the turns after the restored point are gone
 * (they describe edits that no longer exist) and the media map belongs to the
 * content that has just come back.
 *
 * A pruned point is a 404, and the message says the state is gone rather
 * than that the template is. Only the last `MAX_CHECKPOINTS` are held.
 */
export const restoreTemplate = (id, token) =>
  request(`/templates/${id}/restore`, { method: 'POST', body: json({ token }) });

/**
 * `kind` and `lyrics` are what the composer's type selector picked, and they
 * must be LISTED here: this helper destructures its argument, so a field the
 * caller passes and this signature does not name is dropped on the floor —
 * the request succeeds, generation runs, and the selector did nothing.
 *
 * `references` are media-library CLIPS — `[{ media_file_id, start, end }]` —
 * which the worker turns into labelled frames for the model to look at. Ids
 * and a window, never the frames: the extraction is where ffmpeg is, and a
 * job row carrying twelve stills is a row every poll reads back.
 */
/**
 * Something said while a run is going. It is QUEUED — a generation has no
 * turn boundary inside it for a message to be delivered between — and the
 * server answers `pending: true`, which is what the UI must show. Echoing it
 * as sent makes a visible wait look like a swallowed message.
 */
export const addJobContext = (jobId, text) =>
  request(`/jobs/${jobId}/context`, { method: 'POST', body: json({ text }) });

/**
 * `aiMedia` lets the model have pictures GENERATED for image slots — see
 * `mediaGen/`. Named in the signature for the reason `templateId` is below:
 * a field this destructuring does not name never reaches the server.
 */
export const generate = ({
  frames, prompt, images, references, name, kind = 'image', lyrics = false, aiMedia = false, mediaImages = [],
  duration = null,
}) =>
  request('/generate', {
    method: 'POST',
    body: json({
      frames, prompt, images, references, name, kind, lyrics, ai_media: aiMedia, media_images: mediaImages,
      // A video's length, when picked. Null = from the brief — and asked for
      // when the brief does not say.
      ...(kind === 'video' && duration ? { duration } : {}),
    }),
  }).then((b) => b.job);
/**
 * `templateId` is NAMED here, and that is not decoration: this function
 * destructures its argument, so a field the caller passes and the signature
 * does not name is dropped on the floor — exactly how the composer's `kind`
 * shipped doing nothing. It tells the server which SAVED template the run is
 * against, which is what lets the run list name it and open it, including
 * while the run is still going.
 */
export const edit = ({
  template, frame, instruction, selection, images, history, templateId, aiMedia = false,
  mediaImages = [], references = [], answering = false,
}) =>
  request('/edit', {
    method: 'POST',
    body: json({
      template, frame, instruction, selection, images, history, templateId, ai_media: aiMedia,
      media_images: mediaImages, references, answering,
    }),
  }).then((b) => b.job);

/**
 * Answer the question a GENERATION asked. The server makes it again from the
 * original request — layouts, clips and the inspiration images, which only
 * the job row still holds — with the answer attached. → the new job.
 */
export const answerJob = (jobId, text) =>
  request(`/jobs/${jobId}/answer`, { method: 'POST', body: json({ text }) }).then((b) => b.job);

/** What an export was rendered from, and whether its template changed since. */
export const readSnapshot = (id) => request(`/snapshots/${id}`);
/** A new template from an export's snapshot. → the template row. */
export const openSnapshotCopy = (id) =>
  request(`/snapshots/${id}/open`, { method: 'POST' }).then((b) => b.template);
/**
 * A motion template → a video. 503 when the deployment has no browser or no
 * ffmpeg, 422 for a template with no duration, 429 while one is already
 * running — every render shares one worker queue, so they go one at a time.
 */
export const renderVideo = ({
  template, frame, format = 'mp4', fps = 30, duration, audio, save = true, name, templateId,
}) =>
  request('/render-video', {
    method: 'POST',
    body: JSON.stringify({ template, frame, format, fps, duration, audio, save, name, templateId }),
  }).then((b) => b.job);

export const render = ({ template, frame, format = 'png', scale = 1, save = false, name, templateId }) =>
  request('/render', {
    method: 'POST',
    body: json({ template, frame, format, scale, save, name, templateId }),
  }).then((b) => b.job);
export const job = (id) => request(`/jobs/${id}`).then((b) => b.job);

const sleep = (ms, signal) => new Promise((resolve, reject) => {
  const t = setTimeout(resolve, ms);
  signal?.addEventListener('abort', () => { clearTimeout(t); reject(new Error('cancelled')); }, { once: true });
});

/** How long a poll keeps trying through transient failures before it gives up. */
export const POLL_RETRY_BUDGET_MS = 90_000;
const MAX_BACKOFF_MS = 15_000;

/**
 * Poll a job until it is done. Resolves with the job's `result`; rejects
 * with its `error`. `onTick(job)` is called on every poll so a screen can
 * show the status; `signal` (an AbortSignal) stops the loop.
 *
 * ### A dropped request is not a failed job
 *
 * This loop runs for MINUTES — a generation is the model plus Chromium — and
 * it used to abandon the whole run on the first `await job(id)` that threw.
 * One 502 from the gateway, or one fetch cut off because the tab was
 * suspended, and the composer reported a failure while the worker carried on
 * and finished: the result arrived to nobody and the template was never
 * saved. That is the same loss `TemplateRunsContext` exists to prevent, from
 * the other end.
 *
 * So a transient failure backs off and tries again, for up to
 * `POLL_RETRY_BUDGET_MS` of consecutive failure, and the budget resets on
 * every answer. A refusal — 404, a validation error, the job's own `error`
 * status — still rejects at once, because retrying it would only wait.
 *
 * What it says when it does give up names the connection rather than the
 * run, because the run is probably still going: the corner report picks it up
 * from the active list, which is why this is a budget and not forever.
 */
export async function pollJob(id, { onTick, signal, interval = 1500 } = {}) {
  let failingSince = 0;
  let fails = 0;
  for (;;) {
    if (signal?.aborted) throw new Error('cancelled');
    let j;
    try {
      j = await job(id);
    } catch (err) {
      if (!transient(err) || signal?.aborted) throw err;
      const now = Date.now();
      if (!failingSince) failingSince = now;
      if (now - failingSince > POLL_RETRY_BUDGET_MS) {
        const gave = new Error('Lost contact with the server. The run may still be going — check the templates page.');
        gave.transient = true;
        throw gave;
      }
      fails += 1;
      await sleep(Math.min(interval * (2 ** fails), MAX_BACKOFF_MS), signal);
      continue;
    }
    failingSince = 0;
    fails = 0;
    onTick?.(j);
    if (j.status === 'done') return j.result;
    if (j.status === 'error') throw new Error(j.error || 'The job failed.');
    // Without this a stopped run polls for ever: `cancelled` is terminal and
    // is neither a result nor a failure, so it gets its own error to catch.
    if (j.status === 'cancelled') throw new JobCancelled();
    await sleep(interval, signal);
  }
}

/** Start a job and poll it. One call for the three job kinds. */
export const runJob = async (start, opts) => {
  const j = await start();
  return pollJob(j.id, opts);
};

/**
 * The runs: what is going, and what finished while you were elsewhere.
 *
 * `active` is the cheap question the background bubble asks — "is anything
 * running" must not cost a page of history. Neither carries a `result`, which
 * for a generation is a whole template.
 */
export const listJobs = ({ active = false } = {}) =>
  request(`/jobs${active ? '?active=1' : ''}`).then((b) => b.jobs || []);

/** Looked at, so the landing page stops surfacing it. Never un-sees. */
export const markJobSeen = (id) => request(`/jobs/${id}/seen`, { method: 'POST' }).then((b) => b.job);

/**
 * Stop one that is still going. A wrong brief otherwise burns the whole
 * generation. 409 when it has already finished — its result exists.
 */
export const cancelJob = (id) => request(`/jobs/${id}/cancel`, { method: 'POST' }).then((b) => b.job);

// ── media library ──────────────────────────────────────────────────

/** Root images only, which is what the picker shows (same as ContentEditor). */
export const listMediaFiles = () =>
  request('/files', {}, '/media_library').then((b) => (b.files || []).filter((f) => f.media_type === 'image'));

/** The sounds a lyrics slot can be timed against: anything with an audio track. */
/** Clips a brief can point at: "like this", about movement as well as look. */
export const listVideoFiles = () =>
  request('/files', {}, '/media_library')
    .then((b) => (b.files || []).filter((f) => f.media_type === 'video'));

export const listSoundFiles = () =>
  request('/files', {}, '/media_library')
    .then((b) => (b.files || []).filter((f) => f.media_type === 'audio' || f.media_type === 'video'));

/**
 * Whisper's word timestamps and librosa's onsets for one file, both of which
 * the media library already computes for VideoStudio's captions. Started if
 * they have not run, then polled — a transcription is minutes, not seconds.
 *
 * `onTick` reports what it is waiting for, because a silent two-minute wait
 * reads as a broken button.
 */
export const startTranscribe = (id) => request(`/files/${id}/transcribe`, { method: 'POST' }, '/media_library');
export const startRhythm = (id) => request(`/files/${id}/analyze_rhythm`, { method: 'POST' }, '/media_library');

const TRANSCRIBE_TRIES = 60;
const TRANSCRIBE_WAIT = 3000;

export async function soundAnalysis(id, { onTick, wantRhythm = true } = {}) {
  let file = await fileById(id);
  const hasWords = (f) => !!f?.transcript_data?.words?.length;
  const hasRhythm = (f) => !!f?.rhythm_data?.onsets?.length;
  if (!hasWords(file) && file?.transcription_status !== 'processing') await startTranscribe(id).catch(() => {});
  // The rhythm pass is what turns the emphasis heuristic into a measurement,
  // so it is started alongside rather than after: they are different workers.
  if (wantRhythm && !hasRhythm(file) && file?.rhythm_status !== 'processing') await startRhythm(id).catch(() => {});
  for (let i = 0; i < TRANSCRIBE_TRIES; i += 1) {
    if (hasWords(file) && (!wantRhythm || hasRhythm(file) || file?.rhythm_status === 'error')) return file;
    if (file?.transcription_status === 'error') throw new Error('That sound could not be transcribed.');
    onTick?.(hasWords(file) ? 'Measuring the beats…' : 'Transcribing the words…');
    // eslint-disable-next-line no-await-in-loop
    await new Promise((r) => setTimeout(r, TRANSCRIBE_WAIT));
    // eslint-disable-next-line no-await-in-loop
    file = await fileById(id);
  }
  if (hasWords(file)) return file;   // rhythm is optional; words are not
  throw new Error('That sound is taking longer than expected. Try again in a minute.');
}

export const uploadMediaFiles = (fileList) => {
  const form = new FormData();
  for (const f of fileList) form.append('files', f);
  return request('/files/upload', { method: 'POST', body: form }, '/media_library');
};

export const fileById = (id) =>
  request(`/files/${id}`, {}, '/media_library').then((b) => b.file || null);
